import { useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { invokeErrorMessage } from "../../lib/edgeError";

// Dial Pad — text or call any number using a Bardin Twilio line.
// Mounted as a tab on the Notifications page. Calls always go through the
// customer line (470) 823-8816 via twilio-start-call (bridges through the
// caller's cell). Texts default to the customer line (twilio-send-sms) but
// can be flipped to the staff Team line (470) 937-9490 via staff-send-sms
// for staff-to-staff comms — heads-ups, vacation coverage, offboarding notes,
// etc. Notifications for sent texts are handled server-side.

const KEYS: { d: string; sub?: string }[] = [
  { d: "1" }, { d: "2", sub: "ABC" }, { d: "3", sub: "DEF" },
  { d: "4", sub: "GHI" }, { d: "5", sub: "JKL" }, { d: "6", sub: "MNO" },
  { d: "7", sub: "PQRS" }, { d: "8", sub: "TUV" }, { d: "9", sub: "WXYZ" },
  { d: "*" }, { d: "0", sub: "+" }, { d: "#" },
];

function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, "");
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length <= 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  // 11-digit: assume leading 1
  return `+${d.slice(0, 1)} (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 11)}`;
}

function toE164(raw: string): string | null {
  const d = raw.replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  if (raw.startsWith("+") && d.length >= 10) return `+${d}`;
  return null;
}

export default function DialPad() {
  const { member } = useAuth();
  const [raw, setRaw] = useState("");
  const [textBody, setTextBody] = useState("");
  const [mode, setMode] = useState<"call" | "text">("call");
  const [line, setLine] = useState<"customer" | "team">("customer");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: "ok" | "err" | "info"; msg: string } | null>(null);

  const fromLabel =
    mode === "text" && line === "team" ? "(470) 937-9490 · Team line" : "(470) 823-8816";

  const display = useMemo(() => formatPhone(raw), [raw]);
  const e164 = useMemo(() => toE164(raw), [raw]);
  const canSubmit =
    !!e164 &&
    !busy &&
    (mode === "call" || (mode === "text" && textBody.trim().length > 0));

  function press(d: string) {
    // Cap at 11 digits (US 1+10)
    const digits = raw.replace(/\D/g, "");
    if (digits.length >= 11 && /\d/.test(d)) return;
    setRaw((r) => r + d);
    setStatus(null);
  }

  function backspace() {
    setRaw((r) => r.slice(0, -1));
    setStatus(null);
  }

  function clear() {
    setRaw("");
    setTextBody("");
    setStatus(null);
  }

  async function handleCall() {
    if (!e164) return;
    if (!member?.id) {
      setStatus({ kind: "err", msg: "Sign-in required to place a call." });
      return;
    }
    if (!window.confirm(`Call ${display}?\n\nYour cell will ring first, then we'll bridge the call.`)) return;
    setBusy(true);
    setStatus({ kind: "info", msg: "Ringing your cell…" });
    try {
      const { data, error } = await supabase.functions.invoke("twilio-start-call", {
        body: { to_phone: e164, to_name: "Dial Pad", rm_id: member.id },
      });
      if (error) throw new Error(await invokeErrorMessage(error, "Call failed"));
      if (!data?.ok) throw new Error(data?.error || "Call failed");
      setStatus({ kind: "ok", msg: `Call started — answer your cell to connect to ${display}.` });
    } catch (err) {
      setStatus({ kind: "err", msg: "Couldn't start call: " + (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function handleText() {
    if (!e164 || !textBody.trim()) return;
    if (line === "team" && !member?.id) {
      setStatus({ kind: "err", msg: "Sign-in required to send from the Team line." });
      return;
    }
    setBusy(true);
    setStatus({ kind: "info", msg: "Sending…" });
    try {
      const fn = line === "team" ? "staff-send-sms" : "twilio-send-sms";
      const payload =
        line === "team"
          ? {
              to: e164,
              body: textBody.trim(),
              tag: "dial_pad",
              sender_id: member!.id,
            }
          : {
              to: e164,
              body: textBody.trim(),
              tag: "dial_pad",
              sent_by: member?.id ?? null,
            };
      const { data, error } = await supabase.functions.invoke(fn, { body: payload });
      if (error) throw new Error(error.message || "Send failed");
      if (!data?.ok) throw new Error(data?.error || "Send failed");
      const lineLabel = line === "team" ? "Team line" : "customer line";
      setStatus({ kind: "ok", msg: `Sent to ${display} from the ${lineLabel}.` });
      setTextBody("");
    } catch (err) {
      setStatus({ kind: "err", msg: "Couldn't send text: " + (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white rounded-xl shadow p-4 sm:p-6 max-w-md mx-auto">
      <div className="text-center mb-1 text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
        From {fromLabel}
      </div>
      <div className="text-center text-2xl sm:text-3xl font-semibold tabular-nums text-bardin-ink h-10 mb-1 select-all min-h-[40px]">
        {display || <span className="text-slate-300">Enter number</span>}
      </div>
      <div className="text-center text-[11px] text-slate-400 mb-4 h-4">
        {raw && !e164 ? "Enter 10 digits" : ""}
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => { setMode("call"); setStatus(null); }}
          className={
            "flex-1 py-2 rounded-lg text-sm font-semibold transition " +
            (mode === "call"
              ? "bg-bardin-teal text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200")
          }
        >
          📞 Call
        </button>
        <button
          onClick={() => { setMode("text"); setStatus(null); }}
          className={
            "flex-1 py-2 rounded-lg text-sm font-semibold transition " +
            (mode === "text"
              ? "bg-bardin-teal text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200")
          }
        >
          💬 Text
        </button>
      </div>

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {KEYS.map((k) => (
          <button
            key={k.d}
            onClick={() => press(k.d)}
            className="aspect-square rounded-xl bg-slate-50 hover:bg-slate-100 active:bg-slate-200 border border-slate-200 flex flex-col items-center justify-center transition"
            type="button"
          >
            <span className="text-2xl font-semibold text-bardin-ink leading-none">{k.d}</span>
            {k.sub && (
              <span className="text-[9px] tracking-widest text-slate-400 uppercase mt-0.5">{k.sub}</span>
            )}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-3">
        <button
          onClick={backspace}
          disabled={raw.length === 0 || busy}
          className="flex-1 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 transition"
          type="button"
        >
          ⌫ Delete
        </button>
        <button
          onClick={clear}
          disabled={(raw.length === 0 && textBody.length === 0) || busy}
          className="flex-1 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 transition"
          type="button"
        >
          Clear
        </button>
      </div>

      {/* Line picker — only when mode === text. Calls always use customer line. */}
      {mode === "text" && (
        <div className="mb-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setLine("customer"); setStatus(null); }}
              className={
                "flex-1 py-2 rounded-lg text-xs font-semibold transition flex flex-col items-center justify-center " +
                (line === "customer"
                  ? "bg-bardin-teal text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200")
              }
              title="Customer-facing line — goes to homeowners. Auto-notifies the deal RM if the number matches a deal."
            >
              <span>📱 Customer line</span>
              <span className={"text-[10px] tabular-nums " + (line === "customer" ? "opacity-90" : "text-slate-400")}>
                (470) 823-8816
              </span>
            </button>
            <button
              type="button"
              onClick={() => { setLine("team"); setStatus(null); }}
              className={
                "flex-1 py-2 rounded-lg text-xs font-semibold transition flex flex-col items-center justify-center " +
                (line === "team"
                  ? "bg-slate-700 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200")
              }
              title="Staff Team line — for staff-to-staff comms only. Recipient sees the message from the Team number."
            >
              <span>👥 Team line</span>
              <span className={"text-[10px] tabular-nums " + (line === "team" ? "opacity-90" : "text-slate-400")}>
                (470) 937-9490
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Text body input — only when mode === text */}
      {mode === "text" && (
        <textarea
          value={textBody}
          onChange={(e) => setTextBody(e.target.value)}
          placeholder={line === "team" ? "Heads-up for the team…" : "Type your message…"}
          rows={3}
          className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-teal mb-3 resize-none"
          maxLength={1600}
        />
      )}

      {/* Action button */}
      <button
        onClick={mode === "call" ? handleCall : handleText}
        disabled={!canSubmit}
        className={
          "w-full py-3 rounded-lg text-base font-semibold transition " +
          (canSubmit
            ? mode === "call"
              ? "bg-emerald-500 text-white hover:bg-emerald-600"
              : line === "team"
                ? "bg-slate-700 text-white hover:bg-slate-800"
                : "bg-bardin-teal text-white hover:bg-bardin-teal/90"
            : "bg-slate-200 text-slate-400 cursor-not-allowed")
        }
        type="button"
      >
        {busy
          ? mode === "call" ? "Dialing…" : "Sending…"
          : mode === "call"
            ? `📞 Call ${display || "—"}`
            : textBody.trim()
              ? line === "team"
                ? `👥 Send via Team line`
                : `💬 Send via customer line`
              : `💬 Send text (type a message)`}
      </button>

      {/* Status */}
      {status && (
        <div
          className={
            "mt-3 text-sm rounded-lg px-3 py-2 " +
            (status.kind === "ok"
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : status.kind === "err"
                ? "bg-red-50 text-red-700 border border-red-200"
                : "bg-slate-50 text-slate-600 border border-slate-200")
          }
        >
          {status.msg}
        </div>
      )}

      <p className="text-[11px] text-slate-400 mt-4 text-center">
        {mode === "call" ? (
          <>Calls bridge through your cell (must be on file in team_roster).</>
        ) : line === "team" ? (
          <>Team line — for staff-to-staff comms. Recipient sees the message from (470) 937-9490; no deal-owner mirror fires.</>
        ) : (
          <>Customer line — auto-posts a notification to the deal owner if the number matches a deal.</>
        )}
      </p>
    </div>
  );
}
